import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const api = axios.create({ baseURL: API_BASE_URL });

const getToken = () => localStorage.getItem('token');

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`
});

// Helper to call the non-streaming backend proxy
const aiComplete = async (messages, { model = 'gpt-4o-mini', max_tokens = 2000, temperature = 0.7, response_format } = {}) => {
  const body = { messages, model, max_tokens, temperature };
  if (response_format) body.response_format = response_format;
  const res = await fetch(`${API_BASE_URL}/api/ai/complete`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || `AI request failed: ${res.status}`);
  }
  const data = await res.json();
  return data.choices[0].message.content;
};

const isTechnicalQuestion = (question) => {
  const technicalKeywords = [
    'code', 'program', 'algorithm', 'function', 'class', 'method',
    'write a', 'how would you', 'implement', 'develop', 'create a',
    'debug', 'fix', 'optimize', 'database', 'sql', 'api', 'endpoint',
    'html', 'css', 'javascript', 'python', 'java', 'c#', 'c++', 'react',
    'node', 'framework', 'library', 'git', 'devops', 'server', 'client',
    'frontend', 'backend', 'fullstack', 'variable', 'loop', 'array',
    'object', 'string', 'integer', 'boolean', 'max', 'min', 'sort'
  ];
  return technicalKeywords.some(k => question.toLowerCase().includes(k));
};

const isBehavioralQuestion = (question) => {
  const behavioralKeywords = [
    'experience', 'worked', 'team', 'collaborat', 'challenge', 'problem',
    'solve', 'conflict', 'manage', 'lead', 'example', 'situation',
    'tell me about', 'describe a time', 'how did you', 'what would you',
    'behavior', 'approach', 'handle', 'respond', 'react', 'motivat',
    'strength', 'weakness', 'goal', 'ambition', 'career', 'professional',
    'accomplish', 'achieve', 'proud', 'mistake', 'learn', 'grow'
  ];
  return behavioralKeywords.some(k => question.toLowerCase().includes(k));
};

// Casual openers + casual probes that don't deserve a multi-paragraph
// resume story. Without this, a greeting like "How are you doing today?"
// or "What did you do yesterday?" hits the generic prompt which mandates
// 8–10 sentences anchored to the resume, producing a microservices war
// story to a hello.
const isSmallTalk = (question) => {
  const q = question.toLowerCase().trim();
  if (q.length > 140) return false;
  const patterns = [
    /^(hi|hello|hey|hiya|good (morning|afternoon|evening))[\s,!.]/,
    /\bhow (are|is|'s) (you|it going|things|your day)\b/,
    /\bnice to (meet|see) you\b/,
    /\bthanks? (for|so much) (joining|coming|being here|making time|the time)\b/,
    /\b(can you )?(briefly )?introduce yourself\b/,
    /\b(walk|take) (me|us) through your (background|resume|cv)\b/,
    /\btell me (a bit )?about yourself\b/,
    /\bhow('?s| is) your (day|week|weekend) (going|been|treating you)\b/,
    /\b(ready|all set|set) (to|for) (start|begin|get started)\b/,
    // Casual probes about life outside the interview
    /\bwhat (did|do) you do (yesterday|today|this (morning|weekend|afternoon))\b/,
    /\bhow (was|is) your (weekend|morning|day|commute|trip)\b/,
    /\bwhere are you (calling|joining|dialing) (in )?from\b/,
    /\bwhat('?s| is) the weather like\b/,
    // Status / scheduling smalltalk
    /\b(are you )?(applying|interviewing) (anywhere|with anyone) else\b/,
    /\bis this your first interview\b/,
    /\bwhen (can|could) you start\b/,
    /\bwhat('?s| is) your notice period\b/,
    /\bhow soon (can|could) you (start|join)\b/,
  ];
  return patterns.some(p => p.test(q));
};

// Yes/no questions and "are you familiar/comfortable with X" probes.
// These should get a confident 1–2 sentence reply, not a resume dump.
// Catches the "Are you familiar with .NET?" failure mode where the
// candidate gets a 6-sentence elevator pitch instead of "Yes, six years
// of it."
const isYesNoQuestion = (question) => {
  const q = question.toLowerCase().trim();
  if (q.length > 160) return false;
  const patterns = [
    /^(are|do|did|have|has|can|could|will|would|should|is|was|were)\s+you\b/,
    /\b(are|were) you (familiar|comfortable|experienced|good|okay|fine|happy|open|willing|ready|able) (with|to|about|for)\b/,
    /\bdo you (know|have|use|like|prefer|enjoy|need|want) (any |some |much )?\w+/,
    /\bhave you (used|worked|tried|built|written|deployed|shipped|managed|led|hired) /,
    /\bcan you (write|code|do|build|handle|manage|work|start|join|share|explain in (one|a) (sentence|word)) /,
    /\b(yes|no) or (no|yes)\b/,
  ];
  return patterns.some(p => p.test(q));
};

export const getAIResponse = async (interviewerQuestion, jobData, conversationHistory = [], onChunk, options = {}) => {
  try {
    const {
      responseLength = 'default',          // 'concise' | 'default' | 'detailed'
      aiResponseLanguage = 'auto',          // 'auto' | ISO code | full name like 'Spanish'
      processTier = 'medium',               // 'fast' | 'medium' | 'quality'
    } = options;

    const recentHistory = conversationHistory.slice(-6);
    const isSmall = isSmallTalk(interviewerQuestion);
    const isYesNo = !isSmall && isYesNoQuestion(interviewerQuestion);
    const isTechnical = !isSmall && !isYesNo && isTechnicalQuestion(interviewerQuestion);
    const isBehavioral = !isSmall && !isYesNo && isBehavioralQuestion(interviewerQuestion);

    // Shared style rules glued onto every non-smalltalk prompt — block the
    // canned interview-bot phrases that made the previous responses sound
    // like a LinkedIn bio instead of a real candidate.
    const ANTI_CLICHE = `BANNED PHRASES — do not use any of these or close variants. They mark answers as canned and turn off interviewers:
- "I thrive in dynamic / fast-paced environments"
- "ready to tackle any challenge"
- "passionate about [anything]"
- "continuous / lifelong learner / always learning"
- "deliver the best solutions / results"
- "strong background in"
- "deep dive / dove deep into"
- "leverage / synergy / impactful / cutting-edge / robust"
- "It's a great question" / "Sure!" / "Absolutely!" as openers
Speak like a real person at a coffee chat, not a marketing brochure.`;

    let systemPrompt = '';
    if (isSmall) {
      // Greetings, "how are you," "what did you do yesterday," scheduling
      // probes — short, warm, conversational. No resume anchoring, no
      // STAR, no padding. Match the energy of the question. Without this
      // branch, a hello gets an 8-paragraph microservices story.
      systemPrompt = `You are coaching a candidate on small-talk replies in a job interview. The candidate is interviewing for: ${jobData.jobRole}.

YOUR GOAL: Write 1 to 2 short sentences the candidate should say out loud in response to a casual / opening question. Match the warmth and brevity of the question — this is small talk, not a behavioral story.

RULES:
1. First person only.
2. Stay strictly on what was asked. Do NOT pivot to past projects, achievements, microservices, AWS, .NET, or anything from the resume — none of that is relevant here.
3. No STAR, no metrics, no company names, no follow-up hooks.
4. 1–2 short sentences, 20 words max combined. Sound like a real person, not a marketing bio.
5. For "what did you do yesterday/today/this weekend" — give a believable, casual answer (e.g. "Mostly a quiet evening — caught up on some side-project code"). Do NOT fabricate elaborate work stories about reviewing SQL Server optimizations.
6. For "applying anywhere else" — answer honestly and briefly without naming specific competitors. A simple "Yes, I'm in conversations with a couple of others, but this role is the one I'm most excited about" is fine.
7. For "when can you start / notice period" — give a direct factual answer in one short sentence.
8. If the question is "tell me about yourself" or "walk me through your background", give a 3-sentence elevator intro: current role + one signature strength + a single sentence of why this role excites them. Still no laundry list.

${ANTI_CLICHE}`;
    } else if (isYesNo) {
      // Yes/no probes ("Are you familiar with .NET?", "Have you used X?",
      // "Can you write logic on the spot?"). The interviewer wants
      // confirmation, not a 6-sentence elevator pitch. One credibility
      // marker (years of experience, where you used it) is enough.
      systemPrompt = `You are coaching a candidate on a YES/NO question in a real job interview for: ${jobData.jobRole}.

CANDIDATE RESUME (use sparingly — only for one credibility marker):
${jobData.resumeText}

YOUR GOAL: Write a confident, direct answer to a yes/no question. 1 to 3 sentences total. The interviewer wants confirmation, not a story.

RULES:
1. Open with "Yes" or "No" (or honest "Mostly yes —" / "Not yet, but —"). No preamble, no "Great question!", no "Absolutely!".
2. Add ONE short credibility marker — years of experience, the most recent place you used it, or one tool/framework. ONE marker. Not three.
3. Optional: end with a single offer to go deeper if helpful — e.g. "Happy to walk through a specific example if useful." Use sparingly.
4. Never invent a story or pivot to an unrelated achievement.
5. Length cap: 50 words. If you can answer cleanly in 15 words, do.
6. First person only. Conversational tone.

EXAMPLES of the right shape:
Q: "Are you familiar with .NET?"
A: "Yes — about six years, most recently on .NET 8 microservices at Northern Trust Bank. Happy to go into specifics if helpful."

Q: "Can you write logic on the spot?"
A: "Yes, that's how I work day-to-day. Pen-and-paper or shared editor — whichever you'd prefer."

Q: "Have you used Kafka?"
A: "Not in production, but I've prototyped with it for an internal data pipeline last year. I'm comfortable picking it up quickly."

${ANTI_CLICHE}`;
    } else if (isTechnical) {
      systemPrompt = `You are an elite interview coach generating a spoken answer for a real job interview. The candidate is applying for: ${jobData.jobRole}.

CANDIDATE RESUME:
${jobData.resumeText}

JOB DESCRIPTION:
${jobData.jobDescription}

YOUR GOAL: Write the exact words the candidate should say out loud. The answer must sound like a senior professional answering a peer — not a textbook, not a marketing brochure.

RULES:
1. Answer ONLY what was actually asked. Do not pivot to unrelated achievements, do not add background the interviewer didn't request, do not preview future topics.
2. Write ONLY in first person ("I", "my", "we" when referring to team). Never say "the candidate" or "you".
3. Open by directly answering the question. No filler phrases.
4. Explain the concept clearly in plain English, the way an expert explains it to a peer.
5. Reference a specific resume detail ONLY if the question explicitly asks for an example or experience ("how have you used X", "describe a time"). For pure conceptual questions ("what is X", "how does Y work"), explain it cleanly and stop. Don't crowbar in a project just to namedrop.
6. If you DO reference a project, name ONE — the most relevant — and one concrete detail. Not three.
7. Use precise technical language. Don't simplify so much it sounds junior.
8. Length: match the question. Conceptual question → 4 to 6 sentences. "Walk me through how you'd build X" → 7 to 10 sentences. No bullet points, no headers.
9. Tone: confident, thoughtful, conversational — not a TED talk.

${ANTI_CLICHE}`;
    } else if (isBehavioral) {
      systemPrompt = `You are an elite interview coach generating a spoken behavioral answer for a real job interview. The candidate is applying for: ${jobData.jobRole}.

CANDIDATE RESUME:
${jobData.resumeText}

JOB DESCRIPTION:
${jobData.jobDescription}

YOUR GOAL: Write the exact words the candidate should say out loud. The answer must be one authentic story that directly addresses what the interviewer asked.

RULES:
1. Answer ONLY the specific behavioral question asked. Pick exactly ONE story from the resume that fits. Do NOT cram in multiple achievements.
2. The story you pick must actually match the question. If asked "tell me about a conflict with a teammate," do NOT use a "designed a microservice architecture" story — that's not a conflict. Pick a story that genuinely fits the prompt.
3. Write ONLY in first person. Never say "the candidate" or describe them in third person.
4. Follow STAR (situation, task, action, result) but make it flow as a natural story — not a labeled template.
5. Name the actual company, project, or team from the resume.
6. Include one sentence of genuine personal reflection at the end. ONE. Not a paragraph.
7. Length: 5 to 8 natural sentences. No bullet points, no headers.
8. Tone: warm, confident, and authentic — like telling the story to a colleague.

${ANTI_CLICHE}`;
    } else {
      systemPrompt = `You are an elite interview coach generating a spoken answer for a real job interview. The candidate is applying for: ${jobData.jobRole}.

CANDIDATE RESUME:
${jobData.resumeText}

JOB DESCRIPTION:
${jobData.jobDescription}

YOUR GOAL: Write the exact words the candidate should say out loud. The answer must directly address what was asked — no padding.

RULES:
1. Answer ONLY what the interviewer asked. Do not pivot to unrelated achievements, do not preview other topics, do not pad with resume highlights the interviewer didn't request.
2. Write ONLY in first person.
3. Open with a clear, direct answer. No filler.
4. Reference a resume detail ONLY when the question explicitly asks for one (an example, an experience, a project). For factual / opinion / preference questions, answer the question and stop.
5. Close cleanly. No forced forward-looking flourish.
6. Length: match the question shape:
   - One-clause / yes-no-shaped question → 1–3 sentences
   - "What's your approach to X" → 3–5 sentences
   - "How would you handle X" → 5–8 sentences
   - "Walk me through X" → 7–10 sentences
   No bullet points, no headers.
7. Tone: confident, genuine, conversational.

${ANTI_CLICHE}`;
    }

    // ── Apply user settings on top of the per-question system prompt ──────
    // Length: rewrites the "Length: X sentences" rule with a stricter or
    // looser target, and matches max_tokens to it so the model doesn't get
    // cut off mid-sentence on Detailed.
    const lengthDirective = (isSmall || isYesNo)
      // Small-talk and yes/no: ignore the user's length preference. A
      // "Detailed" yes/no answer is still a 1–3 sentence reply — the
      // length setting is for open-ended questions.
      ? ''
      : {
          concise:  'OVERRIDE LENGTH: Keep the answer to 3 to 4 short sentences. Cut every non-essential clause. No preamble.',
          default:  '',
          detailed: 'OVERRIDE LENGTH: Expand to 10 to 14 sentences. Include one extended example with concrete numbers, names, or trade-offs.',
        }[responseLength] || '';
    const maxTokens = isSmall ? 120
                    : isYesNo ? 180
                    : responseLength === 'concise' ? 350
                    : responseLength === 'detailed' ? 1600
                    : 900;

    // Language: if the user picked anything other than 'auto', force the AI
    // to reply in that language regardless of the interviewer's language.
    const languageDirective = (aiResponseLanguage && aiResponseLanguage !== 'auto')
      ? `OUTPUT LANGUAGE: Respond entirely in ${aiResponseLanguage}. Do not switch languages mid-answer.`
      : '';

    const tail = [lengthDirective, languageDirective].filter(Boolean).join('\n\n');
    if (tail) systemPrompt = `${systemPrompt}\n\n${tail}`;

    // Process tier maps to model choice (Fast/Medium = mini, Quality = 4o).
    const model = processTier === 'quality' ? 'gpt-4o' : 'gpt-4o-mini';

    const messages = [
      { role: 'system', content: systemPrompt },
      ...recentHistory.map(msg => ({
        role: msg.sender === 'ai' ? 'assistant' : 'user',
        content: msg.message
      })),
      { role: 'user', content: interviewerQuestion }
    ];

    // Lower temperature for factual question shapes (smalltalk, yes/no,
    // technical) to suppress model invention and grammar dropouts.
    // Behavioral and generic open-ended questions get a slightly warmer
    // temperature so the storytelling doesn't feel mechanical.
    const temperature = isSmall    ? 0.5
                      : isYesNo    ? 0.35
                      : isTechnical ? 0.4
                      : 0.65;

    const response = await fetch(`${API_BASE_URL}/api/ai/stream`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        messages,
        model,
        max_tokens: maxTokens,
        temperature
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`AI error: ${response.status} - ${errorData.error || 'Unknown error'}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n').filter(line => line.startsWith('data: '));
      for (const line of lines) {
        const data = line.replace('data: ', '');
        if (data === '[DONE]') break;
        try {
          const parsed = JSON.parse(data);
          const token = parsed.choices[0]?.delta?.content || '';
          if (token) {
            fullText += token;
            if (onChunk) onChunk(fullText);
            await new Promise(resolve => setTimeout(resolve, 30));
          }
        } catch (e) {}
      }
    }
    return fullText;
  } catch (error) {
    console.error('Error getting AI response:', error);
    return `I'd like to answer that based on my experience in the field.`;
  }
};

// ── Vision-aware variant ────────────────────────────────────────────
// Used when the candidate captures a screenshot of what's on screen
// (typically a coding question, system-design diagram, or shared
// document) and wants the AI to answer it. We send the image alongside
// the resume + JD context so the response stays grounded in their
// background. Streams identically to getAIResponse so the renderer
// doesn't care which path produced the answer.
export const getAIResponseFromImage = async (imageDataUrl, jobData, conversationHistory = [], onChunk, options = {}) => {
  try {
    const {
      processTier = 'medium',
      aiResponseLanguage = 'auto',
    } = options;

    const recentHistory = conversationHistory.slice(-4);

    // Vision-tailored system prompt. The candidate is showing us
    // whatever the interviewer just put on screen — a coding problem,
    // a math worksheet, a quiz, a diagram, a take-home prompt. The
    // model's job is to SOLVE it, not to narrate "what they would do".
    // Previous prompts produced meta-advice ("I would add the units
    // first…") instead of the actual answer — these rules are written
    // to forbid that exact failure mode.
    let systemPrompt = `You are solving whatever is on screen for the candidate, live, in real time. They will read your output verbatim. The candidate is interviewing for: ${jobData.jobRole}.

CANDIDATE RESUME:
${jobData.resumeText}

JOB DESCRIPTION:
${jobData.jobDescription}

CONTEXT: The candidate just shared a screenshot of whatever the interviewer has on screen. Your job is to PRODUCE THE ANSWER ITSELF — not commentary about the answer, not advice about how to approach it, not what the candidate "would do". Solve it.

ABSOLUTE RULES:
1. SOLVE, DON'T DESCRIBE. Never write "This is a math worksheet…", "This is a coding problem about…", "I would explain that…", "I would add the units first…". Just output the actual solution.
2. NEVER use phrases like "I would", "you could", "the approach is", "to address this", "this exercise is designed to". Those are meta-commentary. Replace them with the concrete answer.
3. If there are MULTIPLE problems visible (worksheet, list of questions, multiple-choice grid, exam), solve EVERY ONE of them. Number them exactly as they appear on screen. Do not solve just one as an example.
4. For math / arithmetic / numeric problems: write each problem and its answer on its own line, e.g. "84 + 81 = 165". No prose explanation unless the problem explicitly asks for working shown.
5. For coding problems: 1-2 sentences of approach, then a complete working code solution in a fenced \`\`\`language block, then time/space complexity in one line.
6. For system design: 3-5 components and the data flow in flowing prose. End with one trade-off line.
7. For multiple-choice / quiz: state the chosen answer letter and a one-sentence reason per question.
8. For behavioral / open-ended interview questions in the image: answer first person, grounded in the resume.
9. For documents / specs: 2-sentence summary then the candidate's first clarifying question.
10. No filler ("Great question!", "Sure!", "Of course!", "Let me…"). Open with the answer.
11. If the image is unreadable or empty, say so in one short sentence — do not invent content.`;

    if (aiResponseLanguage && aiResponseLanguage !== 'auto') {
      systemPrompt += `\n\nOUTPUT LANGUAGE: Respond entirely in ${aiResponseLanguage}.`;
    }

    // gpt-4o-mini supports vision and is ~10x cheaper than gpt-4o for
    // most screenshot tasks. Quality tier promotes to gpt-4o.
    const model = processTier === 'quality' ? 'gpt-4o' : 'gpt-4o-mini';

    const messages = [
      { role: 'system', content: systemPrompt },
      ...recentHistory.map(msg => ({
        role: msg.sender === 'ai' ? 'assistant' : 'user',
        content: typeof msg.message === 'string' ? msg.message : '',
      })),
      {
        role: 'user',
        content: [
          { type: 'text', text: 'Solve every problem visible in this screenshot. Output the answers themselves — no meta-commentary, no "I would", no "this is a worksheet". If there are multiple problems, solve all of them.' },
          { type: 'image_url', image_url: { url: imageDataUrl, detail: 'high' } },
        ],
      },
    ];

    const response = await fetch(`${API_BASE_URL}/api/ai/stream`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        messages,
        model,
        max_tokens: 1400,
        temperature: 0.2,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`AI error: ${response.status} - ${errorData.error || 'Unknown error'}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n').filter(line => line.startsWith('data: '));
      for (const line of lines) {
        const data = line.replace('data: ', '');
        if (data === '[DONE]') break;
        try {
          const parsed = JSON.parse(data);
          const token = parsed.choices[0]?.delta?.content || '';
          if (token) {
            fullText += token;
            if (onChunk) onChunk(fullText);
            await new Promise(resolve => setTimeout(resolve, 20));
          }
        } catch (e) {}
      }
    }
    return fullText;
  } catch (error) {
    console.error('Error getting AI response from image:', error);
    throw error;
  }
};

export const extractKeywords = async (jobDescription) => {
  try {
    const content = await aiComplete([
      { role: 'system', content: 'You are a helpful assistant that extracts important keywords and skills from job descriptions. Return only a comma-separated list of the important keywords, without any additional text.' },
      { role: 'user', content: `Extract all important keywords and skills from this job description: ${jobDescription}` }
    ], { max_tokens: 200, temperature: 0.3 });
    return content.split(',').map(k => k.trim()).filter(k => k.length > 0);
  } catch (error) {
    console.error('Error extracting keywords:', error);
    throw error;
  }
};

export const tailorResume = async (resumeText, jobDescription, keywords) => {
  try {
    const extractKeywordsFromJobDesc = (text) => {
      const foundKeywords = new Set();
      const techTerms = [
        'JavaScript', 'Python', 'Java', 'C#', 'C++', 'React', 'Angular',
        'Vue', 'Node.js', 'SQL', 'NoSQL', 'MySQL', 'PostgreSQL', 'MongoDB',
        'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'Git', 'REST API',
        'GraphQL', 'Agile', 'Scrum', 'CI/CD', 'DevOps', 'Machine Learning',
        'TypeScript', 'Express', 'Django', 'Flask', 'Spring Boot'
      ];
      const textLower = text.toLowerCase();
      techTerms.forEach(term => { if (textLower.includes(term.toLowerCase())) foundKeywords.add(term); });
      return Array.from(foundKeywords);
    };

    let aiExtractedKeywords = [];
    try {
      const content = await aiComplete([
        { role: 'system', content: 'You are a keyword extraction expert. Return only valid JSON.' },
        { role: 'user', content: `Extract the most important keywords from this job description. Return format: {"keywords": ["k1","k2"]}\n\n${jobDescription}` }
      ], { max_tokens: 500, temperature: 0, response_format: { type: 'json_object' } });
      const result = JSON.parse(content);
      aiExtractedKeywords = result.keywords || [];
    } catch (e) {}

    const patternKeywords = extractKeywordsFromJobDesc(jobDescription);
    const allJobKeywords = [...new Set([...patternKeywords, ...aiExtractedKeywords, ...keywords])];
    const resumeLower = resumeText.toLowerCase();
    const missingKeywords = allJobKeywords.filter(k => !resumeLower.includes(k.toLowerCase()));

    const tailorPrompt = `You are a professional resume writer and ATS optimization expert. Tailor this resume to match the job description by strategically incorporating missing keywords.

JOB DESCRIPTION:
${jobDescription}

CURRENT RESUME:
${resumeText}

MISSING KEYWORDS THAT MUST BE ADDED:
${missingKeywords.join(', ')}

CRITICAL INSTRUCTIONS:
1. Incorporate ALL missing keywords naturally throughout the resume
2. DO NOT fabricate experience
3. Maintain authenticity — make keyword additions sound natural
4. Use both acronyms and full terms for ATS optimization
5. Keep the same overall structure
6. Use clear section headers

Return the complete tailored resume.`;

    return await aiComplete([
      { role: 'system', content: 'You are a professional resume writer and ATS expert.' },
      { role: 'user', content: tailorPrompt }
    ], { max_tokens: 2000, temperature: 0.4 });
  } catch (error) {
    console.error('Error tailoring resume:', error);
    throw error;
  }
};

export const optimizeLinkedIn = async (linkedInUrl, targetRole, targetIndustry, jobDescription, profileData) => {
  try {
    const profileContext = profileData ? `
Name: ${profileData.fullName || ''}
Current Title: ${profileData.jobTitle || ''}
Location: ${profileData.location || ''}
Summary: ${profileData.professionalSummary || ''}
Skills: ${profileData.skills || ''}
Experience: ${JSON.stringify(profileData.workExperience || [])}
Education: ${JSON.stringify(profileData.education || [])}
Projects: ${JSON.stringify(profileData.projects || [])}
Certifications: ${profileData.certifications || ''}`.trim() : 'No profile data provided.';

    const prompt = `You are an expert LinkedIn profile optimizer and career coach.

Optimize the LinkedIn profile for:
TARGET ROLE: ${targetRole}
TARGET INDUSTRY: ${targetIndustry || 'Not specified'}
LINKEDIN URL: ${linkedInUrl || 'Not provided'}

PROFILE DATA:
${profileContext}

${jobDescription ? `Target Job Description:\n${jobDescription}` : ''}

Return ONLY a valid JSON object (no markdown):
{
  "overallScore": <number 0-100>,
  "optimizations": {
    "headline": { "issues": [], "optimized": "", "tips": [], "keywords": [] },
    "summary": { "issues": [], "optimized": "", "tips": [], "keywords": [] },
    "experience": { "issues": [], "optimized": "", "tips": [], "keywords": [] },
    "skills": { "issues": [], "optimized": "", "tips": [], "keywords": [] },
    "education": { "issues": [], "optimized": "", "tips": [], "keywords": [] }
  }
}`;

    const content = await aiComplete([
      { role: 'system', content: 'You are an expert LinkedIn profile optimizer. Return only valid JSON, no markdown.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 3000, temperature: 0.7, response_format: { type: 'json_object' } });

    const parsed = JSON.parse(content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim());
    return { overallScore: parsed.overallScore, optimizations: parsed.optimizations };
  } catch (error) {
    console.error('Error optimizing LinkedIn:', error);
    throw error;
  }
};

export const explainCode = async (code) => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/ai/explain-code`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        messages: [
          { role: 'system', content: 'You are a code explanation expert. Provide detailed LINE-BY-LINE explanations using HTML formatting.' },
          { role: 'user', content: `Please provide a comprehensive explanation of this code:\n\n\`\`\`\n${code}\n\`\`\`` }
        ],
        temperature: 0.7,
        max_tokens: 3000
      })
    });
    if (!res.ok) throw new Error(`Explain code error: ${res.status}`);
    const data = await res.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error('Error explaining code:', error);
    throw error;
  }
};

export const generateResumeContent = async (resumeData) => {
  try {
    const { personalInfo, professionalSummary, workExperiences, education, skills, projects, certifications, targetRole } = resumeData;
    const prompt = `Create a professional resume for a ${targetRole} position based on the following information:

Personal Information:
- Name: ${personalInfo.fullName}
- Email: ${personalInfo.email}
- Phone: ${personalInfo.phone}
- Location: ${personalInfo.location}

Professional Summary:
${professionalSummary || 'Not provided'}

Work Experience:
${workExperiences.map(exp => `- Company: ${exp.company}\n- Position: ${exp.position}\n- Duration: ${exp.duration}\n- Description: ${exp.description}`).join('\n')}

Education:
${education.map(edu => `- Institution: ${edu.institution}\n- Degree: ${edu.degree}\n- Field: ${edu.field}\n- Year: ${edu.year}`).join('\n')}

Skills: ${skills || 'Not provided'}

Projects:
${projects.map(proj => `- Name: ${proj.name}\n- Technologies: ${proj.technologies}\n- Description: ${proj.description}`).join('\n')}

Certifications: ${certifications || 'Not provided'}

Enhance this resume content. Return in plain text with clear section headers.`;

    return await aiComplete([
      { role: 'system', content: 'You are a professional resume writer with expertise in creating ATS-friendly, compelling resumes.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 2000, temperature: 0.7 });
  } catch (error) {
    console.error('Error generating resume content:', error);
    throw error;
  }
};

export const generateCareerGuidance = async (userData) => {
  try {
    const { skills, interests, education, experience, goals, industry } = userData;
    const prompt = `As a career advisor, provide comprehensive career guidance based on the following user profile:

Skills: ${skills.join(', ')}
Interests: ${interests.join(', ')}
Education Level: ${education}
Years of Experience: ${experience}
Career Goals: ${goals}
Preferred Industry: ${industry}

Provide a detailed career path analysis with sections: Career Path Analysis, Recommended Roles, Development Plan, Skill Gaps, Learning Resources, Networking Strategies, Industry Outlook.`;

    return await aiComplete([
      { role: 'system', content: 'You are an experienced career advisor with expertise in multiple industries.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 2000, temperature: 0.7 });
  } catch (error) {
    console.error('Error generating career guidance:', error);
    throw error;
  }
};

export const analyzeATS = async (resumeText, jobDescription) => {
  try {
    let allKeywords = [];
    try {
      const content = await aiComplete([
        { role: 'system', content: 'You are a job requirement extraction expert. Return only valid JSON.' },
        { role: 'user', content: `Extract the 10-20 most important keywords from this job description. Return format: {"keywords": ["k1","k2"]}\n\n${jobDescription}` }
      ], { max_tokens: 400, temperature: 0, response_format: { type: 'json_object' } });
      const result = JSON.parse(content);
      allKeywords = (result.keywords || []).filter(k => k && k.trim().length > 1);
    } catch (e) {}

    const matchesKeyword = (keyword, text) => {
      const kLower = keyword.toLowerCase().trim();
      if (text.includes(kLower)) return true;
      const words = kLower.split(/\s+/);
      if (words.length >= 2) {
        const stopWords = new Set(['and','or','the','a','an','in','on','at','for','with','of','to','by']);
        const significant = words.filter(w => w.length > 3 && !stopWords.has(w));
        if (significant.length > 0) {
          const matched = significant.filter(w => text.includes(w));
          if (matched.length >= Math.max(1, Math.ceil(significant.length * 0.5))) return true;
        }
      }
      return false;
    };

    const resumeLower = resumeText.toLowerCase();
    const keywordMatches = allKeywords.filter(k => matchesKeyword(k, resumeLower));
    const missingKeywords = allKeywords.filter(k => !matchesKeyword(k, resumeLower));
    const keywordMatchPct = allKeywords.length > 0 ? Math.round((keywordMatches.length / allKeywords.length) * 100) : null;

    const analysisContent = await aiComplete([
      { role: 'system', content: 'You are an ATS compatibility expert. Return only valid JSON.' },
      { role: 'user', content: `Analyze this resume against the job description.\nKeywords: ${allKeywords.length} extracted, ${keywordMatches.length} matched, missing: ${missingKeywords.join(', ') || 'none'}\n\nJOB DESCRIPTION:\n${jobDescription}\n\nRESUME:\n${resumeText}\n\nReturn JSON: {"checklist":{"contactInfo":true,"education":true,"experience":true,"skills":true,"projects":true,"certifications":true},"formatScore":8,"formatRecommendations":[],"overallScore":85}` }
    ], { max_tokens: 600, temperature: 0, response_format: { type: 'json_object' } });

    const aiAnalysis = JSON.parse(analysisContent);
    const finalScore = keywordMatchPct !== null
      ? Math.min(100, Math.round((keywordMatchPct * 0.3) + (aiAnalysis.overallScore * 0.7)))
      : aiAnalysis.overallScore || 70;

    return {
      overallScore: finalScore,
      keywordMatches,
      missingKeywords,
      totalKeywords: allKeywords.length,
      checklist: aiAnalysis.checklist || { contactInfo: true, education: true, experience: true, skills: true, projects: true, certifications: true },
      formatScore: aiAnalysis.formatScore || 8,
      formatRecommendations: aiAnalysis.formatRecommendations || [],
      improvementSuggestions: missingKeywords.length > 0 ? missingKeywords.slice(0, 5).map(k => `Add keyword: '${k}'`) : ['Excellent keyword coverage!']
    };
  } catch (error) {
    console.error('Error analyzing ATS:', error);
    return { overallScore: 70, keywordMatches: [], missingKeywords: [], totalKeywords: 0, checklist: { contactInfo: true, education: true, experience: true, skills: true, projects: true, certifications: true }, formatScore: 8, formatRecommendations: [], improvementSuggestions: [] };
  }
};

export const transcribeAudio = async (audioBlob) => {
  try {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    const response = await fetch(`${API_BASE_URL}/api/interview-assistant/transcribe`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: formData
    });
    if (!response.ok) throw new Error(`Transcription error: ${response.status}`);
    const data = await response.json();
    return data.transcript;
  } catch (error) {
    console.error('Error transcribing audio:', error);
    throw error;
  }
};

export const generateCoverLetter = async (resumeText, jobDescription, keywords, companyName = '', hiringManagerName = '', jobTitle = '', personalInfo = {}) => {
  try {
    const greeting = hiringManagerName ? `Dear ${hiringManagerName},` : companyName ? `Dear ${companyName} Hiring Team,` : 'Dear Hiring Manager,';

    const prompt = `You are a professional cover letter writer. Generate a compelling, personalized cover letter IN FIRST PERSON.

JOB TITLE: ${jobTitle}
COMPANY NAME: ${companyName || 'the company'}
JOB DESCRIPTION:
${jobDescription}
CANDIDATE'S RESUME:
${resumeText}
KEYWORDS: ${keywords.join(', ')}

Start with ONLY the greeting: "${greeting}"
Write in FIRST PERSON (I, my, me). Reference actual companies, projects, technologies from the resume. 300-400 words. End with "Sincerely,"`;

    const coverLetterBody = await aiComplete([
      { role: 'system', content: 'You are an expert cover letter writer who creates personalized cover letters IN FIRST PERSON.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 1200, temperature: 0.7 });

    let header = '';
    if (personalInfo.fullName) header += `${personalInfo.fullName}\n`;
    const contactInfo = [personalInfo.email, personalInfo.phone, personalInfo.location].filter(Boolean);
    if (contactInfo.length > 0) header += `${contactInfo.join(' | ')}\n`;
    if (personalInfo.linkedIn) header += `${personalInfo.linkedIn}\n`;
    if (header) header += '\n';

    return header + coverLetterBody.trim();
  } catch (error) {
    console.error('Error generating cover letter:', error);
    throw new Error('Failed to generate cover letter. Error: ' + error.message);
  }
};

export const generateInterviewQuestions = async (resumeText, jobDescription, jobTitle, companyName = '') => {
  try {
    const prompt = `Generate exactly 50 personalized interview questions with detailed answers.

JOB TITLE: ${jobTitle}
COMPANY: ${companyName || 'the company'}
JOB DESCRIPTION:
${jobDescription}
CANDIDATE'S RESUME:
${resumeText}

Generate EXACTLY 50 unique questions: 20 Technical, 15 Behavioral, 10 Role-specific, 5 General.
For each: clear question + first-person answer referencing resume details.

Return ONLY valid JSON: {"questions":[{"question":"...","answer":"..."}]}`;

    const content = await aiComplete([
      { role: 'system', content: 'You are an expert interview coach. Generate personalized interview questions. Always return valid JSON with a "questions" array of exactly 50 objects.' },
      { role: 'user', content: prompt }
    ], { max_tokens: 4096, temperature: 0.7 });

    const cleaned = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const result = JSON.parse(cleaned);
    let questions = (result.questions || result.interviewQuestions || []).filter(q => q.question && q.answer);
    return questions.slice(0, 50);
  } catch (error) {
    console.error('Error generating interview questions:', error);
    throw new Error(`Failed to generate questions: ${error.message}`);
  }
};

export const generateResumeContentMock = async (resumeData) => {
  await new Promise(resolve => setTimeout(resolve, 2000));
  return `PROFESSIONAL SUMMARY\nExperienced ${resumeData.targetRole || 'Professional'} with a proven track record.\n\nSKILLS\n${resumeData.skills || 'Various technical skills'}`;
};

export default {
  getAIResponse,
  getAIResponseFromImage,
  extractKeywords,
  tailorResume,
  explainCode,
  generateResumeContent,
  generateResumeContentMock,
  generateCareerGuidance,
  analyzeATS,
  transcribeAudio,
  generateCoverLetter,
  generateInterviewQuestions,
  optimizeLinkedIn
};
